/* Second Breakfast Coffee Co. — shared behaviour */

// Mark JS as active (scroll-reveal hiding is gated on this)
document.documentElement.classList.add("js");

// Mobile navigation
const toggle = document.querySelector(".nav-toggle");
const nav = document.querySelector(".main-nav");
if (toggle && nav) {
  const closeMenu = () => {
    nav.classList.remove("open");
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Open menu");
    document.body.style.overflow = "";
  };

  toggle.addEventListener("click", () => {
    const open = nav.classList.toggle("open");
    toggle.setAttribute("aria-expanded", open);
    toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    document.body.style.overflow = open ? "hidden" : "";
  });
  nav.querySelectorAll("a").forEach((a) =>
    a.addEventListener("click", closeMenu)
  );
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && nav.classList.contains("open")) {
      closeMenu();
      toggle.focus();
    }
  });
}

// Scroll reveal
const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add("in");
        observer.unobserve(e.target);
      }
    });
  },
  { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
);
document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

// Footer year
document.querySelectorAll("[data-year]").forEach((el) => {
  el.textContent = new Date().getFullYear();
});

let cartNoticeTimer;
const showCartNotice = (message, isError = false, title = "") => {
  let notice = document.querySelector("[data-cart-notice]");

  if (!notice) {
    notice = document.createElement("aside");
    notice.className = "cart-notice";
    notice.dataset.cartNotice = "";
    notice.setAttribute("role", "status");
    notice.setAttribute("aria-live", "polite");
    notice.setAttribute("aria-hidden", "true");
    notice.innerHTML = `
      <span class="cart-notice__icon" aria-hidden="true">✓</span>
      <div class="cart-notice__content">
        <strong data-cart-notice-title>Added to your satchel</strong>
        <span data-cart-notice-message></span>
      </div>
      <a class="cart-notice__link" data-cart-notice-link>View Cart</a>
      <button class="cart-notice__close" type="button" aria-label="Close notification">×</button>
    `;
    notice.querySelector("[data-cart-notice-link]").href = `${window.Shopify?.routes?.root || "/"}cart`;
    notice.querySelector(".cart-notice__close").addEventListener("click", () => {
      notice.classList.remove("is-visible");
      notice.setAttribute("aria-hidden", "true");
    });
    document.body.appendChild(notice);
  }

  notice.classList.toggle("is-error", isError);
  notice.querySelector("[data-cart-notice-title]").textContent = title || (isError
    ? "Could not add item"
    : "Added to your satchel");
  notice.querySelector("[data-cart-notice-message]").textContent = message;
  notice.querySelector("[data-cart-notice-link]").hidden = isError;
  notice.setAttribute("aria-hidden", "false");

  window.clearTimeout(cartNoticeTimer);
  window.requestAnimationFrame(() => notice.classList.add("is-visible"));
  cartNoticeTimer = window.setTimeout(() => {
    notice.classList.remove("is-visible");
    notice.setAttribute("aria-hidden", "true");
  }, 4500);
};

try {
  const savedNotice = window.sessionStorage.getItem("cartNotice");
  if (savedNotice) {
    window.sessionStorage.removeItem("cartNotice");
    const notice = JSON.parse(savedNotice);
    if (notice?.message) showCartNotice(notice.message, false, notice.title || "");
  }
} catch (_) {
  // Cart actions still work when session storage is unavailable.
}

// Add products without leaving the current page.
document.addEventListener("submit", async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const action = new URL(form.action, window.location.origin);
  if (!action.pathname.endsWith("/cart/add")) return;

  event.preventDefault();
  if (form.dataset.cartAdding === "true") return;
  form.dataset.cartAdding = "true";

  const submitButton = event.submitter || form.querySelector('[type="submit"]');
  const originalLabel = submitButton ? submitButton.textContent.trim() : "";

  if (submitButton) {
    submitButton.disabled = true;
    submitButton.setAttribute("aria-busy", "true");
    submitButton.textContent = "Adding…";
  }

  try {
    const response = await fetch(`${action.pathname}.js`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(form),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.description || result.message || "Unable to add this item.");
    }
    const addedItem = await response.json();

    const cartRoot = window.Shopify?.routes?.root || "/";
    try {
      const cartResponse = await fetch(`${cartRoot}cart.js`, {
        headers: { Accept: "application/json" },
      });
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const cartLink = document.querySelector("[data-cart-link]");
        let cartCount = cartLink?.querySelector("[data-cart-count]");

        if (cartLink) {
          cartLink.setAttribute("aria-label", `Cart (${cart.item_count} items)`);
          if (!cartCount && cart.item_count > 0) {
            cartCount = document.createElement("span");
            cartCount.className = "cart-count";
            cartCount.dataset.cartCount = "";
            cartCount.setAttribute("aria-hidden", "true");
            cartLink.appendChild(cartCount);
          }
          if (cartCount) cartCount.textContent = String(cart.item_count);
        }
      }
    } catch (_) {
      // The item was added successfully; a count refresh should not report failure.
    }

    if (submitButton) submitButton.textContent = "Added!";
    showCartNotice(`${addedItem.product_title || addedItem.title || "Item"} is now in your cart.`);
  } catch (error) {
    const message = error.message || "Please try again.";
    if (submitButton) submitButton.textContent = "Try again";
    showCartNotice(message, true);
  } finally {
    if (submitButton) {
      submitButton.removeAttribute("aria-busy");
      window.setTimeout(() => {
        submitButton.disabled = false;
        submitButton.textContent = originalLabel;
        delete form.dataset.cartAdding;
      }, 1600);
    } else {
      delete form.dataset.cartAdding;
    }
  }
});

// Traveler's Supply subscription crate. Scoped so it is inert on other pages.
const initSupplyCrate = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.cratePickerReady === "true") return;

  const picker = root.querySelector("[data-crate-picker]");
  const form = root.querySelector("[data-crate-form]");
  if (!picker || !form) return;

  const cards = Array.from(picker.querySelectorAll(".crate-coffee"));
  const counter = picker.querySelector("[data-crate-counter]");
  const submit = picker.querySelector("[data-crate-submit]");
  const lede = picker.querySelector("[data-crate-lede]");
  const frequency = picker.querySelector("[data-crate-frequency]");
  const frequencyLabel = picker.querySelector("[data-crate-frequency-label]");
  const benefit = picker.querySelector("[data-crate-benefit]");
  const total = picker.querySelector("[data-crate-total]");
  const errorElement = picker.querySelector("[data-crate-error]");
  const closeButton = picker.querySelector("[data-crate-close]");
  const requiredElements = [counter, submit, lede, frequency, frequencyLabel, benefit, total, errorElement, closeButton];
  if (requiredElements.some((element) => !element)) return;

  root.dataset.cratePickerReady = "true";

  const shopRoot = window.Shopify?.routes?.root || "/";
  const cartAddUrl = root.dataset.cartAddUrl || `${shopRoot}cart/add.js`;
  const cartUpdateUrl = root.dataset.cartUpdateUrl || `${shopRoot}cart/update.js`;
  const cartUrl = root.dataset.cartUrl || `${shopRoot}cart`;
  let required = 0;
  let opener = null;
  let currentTier = "";
  let discountPercent = 0;
  let discountCode = "";
  let freeShipping = false;
  let shippingDiscountCode = "";
  let selections = [];
  let nextSelectionId = 1;

  const money = (cents) => (cents / 100).toFixed(2);
  const cadenceLabel = () => frequency.selectedIndex >= 0
    ? frequency.options[frequency.selectedIndex].textContent.trim()
    : "Subscription";
  const getVariants = (card) => {
    const variantData = card.querySelector("[data-coffee-variants]");
    if (!variantData) return [];
    try {
      return JSON.parse(variantData.textContent);
    } catch (_) {
      return [];
    }
  };
  const numberWords = {
    one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
    seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  };
  const unitWeights = { day: 1, week: 7, month: 30, year: 365 };
  const normalizeAmount = (value) => Number.parseInt(value, 10)
    || numberWords[String(value).toLowerCase()]
    || 1;
  const planCadence = (plan) => {
    const options = Array.isArray(plan.options) ? plan.options : [];
    const deliveryOptions = options.filter((option) => /delivery/i.test(option.name || ""));
    const frequencyOptions = options.filter((option) => /frequency/i.test(option.name || ""));
    const relevantOptions = deliveryOptions.length > 0
      ? deliveryOptions
      : (frequencyOptions.length > 0 ? frequencyOptions : options);
    const optionText = relevantOptions.map((option) => option.value).filter(Boolean).join(" ");
    const text = (optionText || plan.name || "").trim();
    const adjectiveIntervals = {
      daily: ["day", 1], weekly: ["week", 1], biweekly: ["week", 2],
      monthly: ["month", 1], quarterly: ["month", 3],
      yearly: ["year", 1], annually: ["year", 1],
    };
    const adjective = Object.keys(adjectiveIntervals).find((label) =>
      new RegExp(`\\b${label}\\b`, "i").test(text)
    );
    let amount;
    let unit;

    if (adjective) {
      [unit, amount] = adjectiveIntervals[adjective];
    } else {
      const interval = text.match(/(?:deliver(?:y)?\s+)?every\s+(?:(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+)?(day|week|month|year)s?\b/i)
        || text.match(/\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)[ -](day|week|month|year)s?\b/i)
        || text.match(/^\s*(day|week|month|year)s?\s*$/i);
      if (interval) {
        if (interval.length === 2) {
          amount = 1;
          unit = interval[1].toLowerCase();
        } else {
          amount = normalizeAmount(interval[1]);
          unit = interval[2].toLowerCase();
        }
      }
    }

    if (unit && amount) {
      return {
        key: `${amount}:${unit}`,
        label: `Every ${amount === 1 ? "" : `${amount} `}${unit}${amount === 1 ? "" : "s"}`,
        order: amount * unitWeights[unit],
      };
    }

    const fallbackLabel = text || "Subscription";
    return {
      key: `plan:${fallbackLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`,
      label: fallbackLabel,
      order: Number.MAX_SAFE_INTEGER,
    };
  };
  const findSellingPlan = (variant, requestedFrequency = frequency.value) => {
    if (!variant || !requestedFrequency) return null;
    const plans = Array.isArray(variant.sellingPlans) ? variant.sellingPlans : [];
    return plans.find((plan) => planCadence(plan).key === requestedFrequency) || null;
  };
  const buildFrequencyOptions = () => {
    const cadences = new Map();
    let dynamicLabel = "";

    cards.flatMap(getVariants).forEach((variant) => {
      if (!variant.available) return;
      (variant.sellingPlans || []).forEach((plan) => {
        if (dynamicLabel === "") {
          const options = Array.isArray(plan.options) ? plan.options : [];
          const namedOption = options.find((option) => /delivery/i.test(option.name || ""))
            || options.find((option) => /frequency/i.test(option.name || ""))
            || options[0];
          dynamicLabel = namedOption?.name || "";
        }
        const cadence = planCadence(plan);
        if (!cadences.has(cadence.key)) cadences.set(cadence.key, cadence);
      });
    });

    frequencyLabel.textContent = dynamicLabel || "Delivery frequency";
    const sortedCadences = Array.from(cadences.values()).sort(
      (first, second) => first.order - second.order || first.label.localeCompare(second.label)
    );
    frequency.replaceChildren();

    if (sortedCadences.length === 0) {
      const option = document.createElement("option");
      option.value = "";
      option.textContent = "No delivery frequencies available";
      frequency.appendChild(option);
      frequency.disabled = true;
      return;
    }

    sortedCadences.forEach((cadence) => {
      const option = document.createElement("option");
      option.value = cadence.key;
      option.textContent = cadence.label;
      frequency.appendChild(option);
    });
    frequency.disabled = false;
  };
  const syncCardVariant = (card) => {
    const variants = getVariants(card);
    const selectedOptions = Array.from(card.querySelectorAll("[data-coffee-option]")).map(
      (select) => select.value
    );
    const variant = selectedOptions.length === 0
      ? variants[0]
      : variants.find((candidate) =>
        candidate.options.every((value, index) => value === selectedOptions[index])
      );
    const sellingPlan = findSellingPlan(variant);
    const available = Boolean(variant && variant.available && sellingPlan);
    const variantTitle = variant && variant.title !== "Default Title" ? variant.title : "";

    card.dataset.variantAvailable = String(available);
    card.dataset.variantId = variant ? String(variant.id) : "";
    card.dataset.sellingPlan = sellingPlan ? String(sellingPlan.id) : "";
    card.dataset.sellingPlanName = sellingPlan ? sellingPlan.name : "";
    card.dataset.price = variant ? String((sellingPlan && sellingPlan.price) || variant.price || 0) : "0";
    card.dataset.variantTitle = variantTitle || "Standard option";
    card.dataset.selection = variantTitle
      ? `${card.dataset.coffee} — ${variantTitle}`
      : card.dataset.coffee;

    const availability = card.querySelector("[data-crate-availability]");
    const priceLabel = card.querySelector("[data-crate-price]");
    if (availability) {
      availability.hidden = available;
      availability.textContent = `${cadenceLabel()} is unavailable for this option.`;
    }
    if (priceLabel) {
      priceLabel.textContent = `${money(Number.parseInt(card.dataset.price, 10) || 0)} / delivery · ${cadenceLabel().toLowerCase()}`;
    }
    card.classList.toggle("is-unavailable", !available);
  };
  const renderSelections = () => {
    cards.forEach((card) => {
      const cardSelections = selections.filter((selection) => selection.card === card);
      const list = card.querySelector("[data-crate-choice-list]");
      const count = card.querySelector("[data-crate-card-count]");
      if (!list || !count) return;
      list.replaceChildren();

      cardSelections.forEach((selection) => {
        const row = document.createElement("div");
        row.className = "crate-choice";
        const label = document.createElement("span");
        label.textContent = selection.optionLabel;
        const remove = document.createElement("button");
        remove.type = "button";
        remove.dataset.crateRemoveChoice = String(selection.uid);
        remove.setAttribute("aria-label", `Remove ${selection.label}`);
        remove.textContent = "×";
        row.append(label, remove);
        list.appendChild(row);
      });

      card.classList.toggle("selected", cardSelections.length > 0);
      count.textContent = cardSelections.length > 0
        ? `✦ ${cardSelections.length} ${cardSelections.length === 1 ? "choice" : "choices"} added`
        : "";
    });
  };
  const refresh = () => {
    const full = selections.length === required;
    const subtotal = selections.reduce((sum, selection) => sum + selection.price, 0);
    const estimatedTotal = Math.round(subtotal * (100 - discountPercent) / 100);

    cards.forEach((card) => {
      const addButton = card.querySelector("[data-crate-add-choice]");
      if (addButton) {
        addButton.disabled = card.dataset.variantAvailable !== "true" || selections.length >= required;
      }
    });

    renderSelections();
    counter.textContent = `${selections.length} of ${required} chosen`;
    counter.classList.toggle("full", full);
    submit.disabled = !full;
    const benefits = [cadenceLabel()];
    if (discountPercent > 0) benefits.push(`${discountPercent}% off`);
    if (freeShipping) benefits.push("Free shipping");
    benefit.textContent = benefits.join(" + ");
    total.textContent = `${money(estimatedTotal)} estimated / delivery`;
  };
  const reset = () => {
    selections = [];
    nextSelectionId = 1;
    errorElement.textContent = "";
    errorElement.classList.remove("show");
    submit.dataset.viewCart = "false";
    submit.textContent = "Start Subscription";
    refresh();
  };
  const handleEscape = (event) => {
    if (event.key === "Escape") close();
  };
  const open = (tier) => {
    opener = tier.querySelector(".crate-choose") || tier;
    required = Number.parseInt(tier.dataset.bags, 10) || 1;
    currentTier = tier.dataset.tier || "";
    discountPercent = Number.parseInt(tier.dataset.discountPercent, 10) || 0;
    discountCode = tier.dataset.discountCode || "";
    freeShipping = tier.dataset.freeShipping === "true";
    shippingDiscountCode = tier.dataset.shippingDiscountCode || "";
    lede.textContent = `Choose exactly ${required} ${required === 1 ? "product" : "products"} for your ${currentTier} subscription.`;
    reset();
    picker.hidden = false;
    picker.classList.add("open");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    closeButton.focus();
  };
  const close = () => {
    picker.classList.remove("open");
    picker.hidden = true;
    document.body.style.overflow = "";
    document.removeEventListener("keydown", handleEscape);
    if (opener?.isConnected) opener.focus();
  };

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (submit.dataset.viewCart === "true") {
      window.location.assign(cartUrl);
      return;
    }
    if (selections.length !== required) return;

    const originalLabel = submit.textContent;
    let itemsAdded = false;
    submit.disabled = true;
    submit.textContent = "Adding Subscriptions…";
    errorElement.textContent = "";
    errorElement.classList.remove("show");
    const benefitLabel = [
      discountPercent > 0 ? `${discountPercent}% off` : "",
      freeShipping ? "Free shipping" : "",
    ].filter(Boolean).join(" + ");
    const items = selections.map((selection) => ({
      id: selection.variantId,
      quantity: 1,
      selling_plan: selection.sellingPlan,
      properties: {
        "Provision plan": currentTier,
        "Subscription cadence": selection.cadence,
        "Plan benefit": benefitLabel,
        "Selected choice": selection.label,
      },
    }));

    try {
      const addResponse = await fetch(cartAddUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!addResponse.ok) {
        const failure = await addResponse.json().catch(() => ({}));
        throw new Error(failure.description || "The selected subscriptions could not be added.");
      }
      itemsAdded = true;

      const discountCodes = [discountCode, shippingDiscountCode].filter(Boolean);
      if (discountCodes.length > 0) {
        const discountResponse = await fetch(cartUpdateUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ discount: discountCodes.join(",") }),
        });
        if (!discountResponse.ok) {
          throw new Error("The products were added, but the package discount could not be applied.");
        }
      }

      window.location.assign(cartUrl);
    } catch (error) {
      errorElement.textContent = error.message || "Something went wrong. Please try again.";
      errorElement.classList.add("show");
      submit.disabled = false;
      if (itemsAdded) {
        submit.dataset.viewCart = "true";
        submit.textContent = "View Cart";
      } else {
        submit.textContent = originalLabel;
      }
    }
  });

  root.querySelectorAll("[data-crate-opener]").forEach((tier) => {
    tier.addEventListener("click", () => open(tier));
  });

  buildFrequencyOptions();
  cards.forEach((card) => {
    syncCardVariant(card);
    const addButton = card.querySelector("[data-crate-add-choice]");
    addButton?.addEventListener("click", () => {
      if (card.dataset.variantAvailable !== "true" || selections.length >= required) return;
      selections.push({
        uid: nextSelectionId++,
        card,
        product: card.dataset.coffee,
        label: card.dataset.selection,
        optionLabel: card.dataset.variantTitle,
        variantId: Number.parseInt(card.dataset.variantId, 10),
        sellingPlan: Number.parseInt(card.dataset.sellingPlan, 10),
        sellingPlanName: card.dataset.sellingPlanName,
        cadence: cadenceLabel(),
        price: Number.parseInt(card.dataset.price, 10) || 0,
      });
      refresh();
    });
    card.querySelectorAll("[data-coffee-option]").forEach((select) => {
      select.addEventListener("change", () => {
        syncCardVariant(card);
        refresh();
      });
    });
  });

  frequency.addEventListener("change", () => {
    const previousSelectionCount = selections.length;
    selections = selections.flatMap((selection) => {
      const variant = getVariants(selection.card).find(
        (candidate) => Number(candidate.id) === selection.variantId
      );
      const sellingPlan = findSellingPlan(variant);
      if (!variant || !variant.available || !sellingPlan) return [];
      return [{
        ...selection,
        sellingPlan: Number.parseInt(sellingPlan.id, 10),
        sellingPlanName: sellingPlan.name,
        cadence: cadenceLabel(),
        price: Number.parseInt(sellingPlan.price, 10) || variant.price || 0,
      }];
    });
    cards.forEach(syncCardVariant);
    errorElement.textContent = "";
    errorElement.classList.remove("show");
    if (selections.length < previousSelectionCount) {
      errorElement.textContent = `Some choices were removed because they are not available ${cadenceLabel().toLowerCase()}.`;
      errorElement.classList.add("show");
    }
    refresh();
  });

  picker.addEventListener("click", (event) => {
    const remove = event.target.closest("[data-crate-remove-choice]");
    if (remove) {
      const uid = Number.parseInt(remove.dataset.crateRemoveChoice, 10);
      selections = selections.filter((selection) => selection.uid !== uid);
      refresh();
    } else if (event.target === picker) {
      close();
    }
  });
  closeButton.addEventListener("click", close);
  root.cratePickerCleanup = () => {
    document.removeEventListener("keydown", handleEscape);
    if (!picker.hidden) document.body.style.overflow = "";
  };
};

const initSupplyCrates = (scope) => {
  const roots = scope.matches?.("[data-supply-section]")
    ? [scope]
    : Array.from(scope.querySelectorAll?.("[data-supply-section]") || []);
  roots.forEach(initSupplyCrate);
};

initSupplyCrates(document);
document.addEventListener("shopify:section:load", (event) => initSupplyCrates(event.target));
document.addEventListener("shopify:section:unload", (event) => {
  const root = event.target.matches?.("[data-supply-section]")
    ? event.target
    : event.target.querySelector?.("[data-supply-section]");
  root?.cratePickerCleanup?.();
});

// Cart quantity controls. Scoped so the handler is inactive outside the cart section.
const initCartPage = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.cartPageReady === "true") return;
  const form = root.querySelector("[data-cart-form]");
  if (!form) return;
  root.dataset.cartPageReady = "true";

  form.addEventListener("click", async (event) => {
    const removeButton = event.target.closest(".cart-item-remove__link");
    if (!removeButton || !form.contains(removeButton) || removeButton.dataset.removing === "true") return;
    event.preventDefault();

    const isPackage = removeButton.matches("[data-remove-package]");
    const itemName = removeButton.dataset.removeName || (isPackage ? "Adventure Box" : "Item");
    const packageCard = isPackage ? removeButton.closest("[data-adventure-package]") : null;
    const keys = isPackage
      ? Array.from(packageCard?.querySelectorAll("[data-package-key]") || [])
        .map((item) => item.dataset.packageKey)
        .filter(Boolean)
      : [removeButton.dataset.removeKey].filter(Boolean);
    if (!keys.length) return;

    const updates = Object.fromEntries(keys.map((key) => [key, 0]));
    const updateUrl = root.dataset.cartUpdateUrl || `${window.Shopify?.routes?.root || "/"}cart/update.js`;
    const originalLabel = removeButton.textContent;
    removeButton.dataset.removing = "true";
    removeButton.setAttribute("aria-disabled", "true");
    removeButton.setAttribute("aria-busy", "true");
    removeButton.textContent = "…";
    form.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(updateUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || result.message || "The item could not be removed.");
      }
      try {
        window.sessionStorage.setItem("cartNotice", JSON.stringify({
          title: "Removed from your satchel",
          message: isPackage
            ? `${itemName} and its coffees were removed from your cart.`
            : `${itemName} was removed from your cart.`,
        }));
      } catch (_) {
        // Reloading the cart remains the source of truth if storage is unavailable.
      }
      window.location.reload();
    } catch (error) {
      delete removeButton.dataset.removing;
      removeButton.removeAttribute("aria-disabled");
      removeButton.removeAttribute("aria-busy");
      removeButton.textContent = originalLabel;
      form.removeAttribute("aria-busy");
      showCartNotice(error.message || "Please try again.", true, "Could not update cart");
    }
  });

  form.addEventListener("click", (event) => {
    const button = event.target.closest(".cart-qty-btn");
    if (!button || button.disabled || !form.contains(button)) return;
    event.preventDefault();

    const quantity = button.closest(".cart-item-qty");
    const display = quantity?.querySelector(".cart-qty-val");
    const input = quantity?.querySelector("[data-qty-input]");
    if (!quantity || !display || !input) return;

    let nextQuantity = Number.parseInt(input.value, 10) || 1;
    if (button.dataset.action === "increase") {
      nextQuantity += 1;
    } else if (nextQuantity > 1) {
      nextQuantity -= 1;
    } else {
      return;
    }

    input.value = String(nextQuantity);
    display.textContent = String(nextQuantity);
    form.setAttribute("aria-busy", "true");
    form.querySelectorAll(".cart-qty-btn").forEach((control) => {
      control.disabled = true;
    });
    window.requestAnimationFrame(() => form.requestSubmit());
  });
};

const initCartPages = (scope) => {
  const roots = scope.matches?.("[data-cart-page]")
    ? [scope]
    : Array.from(scope.querySelectorAll?.("[data-cart-page]") || []);
  roots.forEach(initCartPage);
};

initCartPages(document);
document.addEventListener("shopify:section:load", (event) => initCartPages(event.target));

// Arrange a Gift picker. Scoped so it is inert outside gift sections.
const initGiftPicker = (picker) => {
  if (!(picker instanceof HTMLElement) || picker.dataset.giftPickerReady === "true") return;

  const root = picker.closest(".shopify-section") || picker.parentElement;
  const openButton = root?.querySelector("[data-gift-open]");
  const closeButton = picker.querySelector("[data-gift-close]");
  const checkoutButton = picker.querySelector("[data-gift-checkout]");
  const subtotalElement = picker.querySelector("[data-gift-subtotal]");
  const errorElement = picker.querySelector("[data-gift-error]");
  if (!openButton || !closeButton || !checkoutButton || !subtotalElement || !errorElement) return;

  picker.dataset.giftPickerReady = "true";
  const rows = Array.from(picker.querySelectorAll("[data-gift-qty]"));
  const currencySymbol = picker.dataset.currencySymbol || "$";
  const cartAddUrl = picker.dataset.cartAddUrl || `${window.Shopify?.routes?.root || "/"}cart/add.js`;
  const checkoutUrl = picker.dataset.checkoutUrl || `${window.Shopify?.routes?.root || "/"}checkout`;
  let returnFocus = openButton;

  const money = (cents) => `${currencySymbol}${(cents / 100).toFixed(2)}`;
  const sync = () => {
    let subtotal = 0;
    let quantity = 0;
    rows.forEach((row) => {
      const countElement = row.querySelector("[data-gift-count]");
      const count = Number.parseInt(countElement?.textContent, 10) || 0;
      const price = Number.parseInt(row.dataset.price, 10) || 0;
      subtotal += count * price;
      quantity += count;
      row.closest("[data-gift-item]")?.classList.toggle("selected", count > 0);
    });
    subtotalElement.textContent = money(subtotal);
    checkoutButton.disabled = quantity === 0;
  };

  rows.forEach((row) => {
    const countElement = row.querySelector("[data-gift-count]");
    const item = row.closest("[data-gift-item]");
    const variantSelect = item?.querySelector("[data-gift-variant-select]");
    const priceLabel = item?.querySelector("[data-gift-price-label]");
    const plusButton = row.querySelector("[data-gift-plus]");
    const minusButton = row.querySelector("[data-gift-minus]");
    if (!countElement || !plusButton || !minusButton) return;

    variantSelect?.addEventListener("change", () => {
      const option = variantSelect.selectedOptions[0];
      if (!option) return;
      row.dataset.variantId = variantSelect.value;
      row.dataset.price = option.dataset.price || "0";
      if (priceLabel) priceLabel.textContent = money(Number.parseInt(row.dataset.price, 10) || 0);
      countElement.textContent = "0";
      sync();
    });

    plusButton.addEventListener("click", () => {
      const current = Number.parseInt(countElement.textContent, 10) || 0;
      countElement.textContent = String(Math.min(99, current + 1));
      sync();
    });
    minusButton.addEventListener("click", () => {
      const current = Number.parseInt(countElement.textContent, 10) || 0;
      countElement.textContent = String(Math.max(0, current - 1));
      sync();
    });
  });

  const close = () => {
    picker.classList.remove("open");
    picker.hidden = true;
    document.body.style.overflow = "";
    document.removeEventListener("keydown", handleKeydown);
    if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus();
  };

  const handleKeydown = (event) => {
    if (event.key === "Escape") {
      close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(picker.querySelectorAll(
      'button:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      picker.querySelector(".gift-picker-panel")?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const open = () => {
    returnFocus = document.activeElement;
    picker.hidden = false;
    picker.classList.add("open");
    document.body.style.overflow = "hidden";
    errorElement.classList.remove("show");
    document.addEventListener("keydown", handleKeydown);
    closeButton.focus();
  };

  openButton.addEventListener("click", open);
  closeButton.addEventListener("click", close);
  picker.addEventListener("click", (event) => {
    if (event.target === picker) close();
  });

  checkoutButton.addEventListener("click", async () => {
    const items = rows
      .map((row) => ({
        id: Number.parseInt(row.dataset.variantId, 10),
        quantity: Number.parseInt(row.querySelector("[data-gift-count]")?.textContent, 10) || 0,
        properties: { "_Gift order": "Arrange a Gift" },
      }))
      .filter((item) => item.id && item.quantity > 0);
    if (items.length === 0) return;

    checkoutButton.disabled = true;
    checkoutButton.setAttribute("aria-busy", "true");
    checkoutButton.textContent = "Adding…";
    errorElement.classList.remove("show");

    try {
      const response = await fetch(cartAddUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || result.message || "Cart add failed");
      }
      window.location.assign(checkoutUrl);
    } catch (error) {
      errorElement.textContent = error.message || "Something went wrong adding your gift to the cart. Please try again.";
      errorElement.classList.add("show");
      checkoutButton.disabled = false;
      checkoutButton.removeAttribute("aria-busy");
      checkoutButton.textContent = "Checkout Now";
    }
  });

  picker.giftPickerCleanup = () => {
    document.removeEventListener("keydown", handleKeydown);
    if (!picker.hidden) document.body.style.overflow = "";
  };
  sync();
};

const initGiftPickers = (scope) => {
  const pickers = scope.matches?.("[data-gift-picker]")
    ? [scope]
    : Array.from(scope.querySelectorAll?.("[data-gift-picker]") || []);
  pickers.forEach(initGiftPicker);
};

initGiftPickers(document);
document.addEventListener("shopify:section:load", (event) => initGiftPickers(event.target));
document.addEventListener("shopify:section:unload", (event) => {
  const picker = event.target.matches?.("[data-gift-picker]")
    ? event.target
    : event.target.querySelector?.("[data-gift-picker]");
  picker?.giftPickerCleanup?.();
});

// Adventure Box Builder. A separate, optional section that requires three unique coffees.
const initAdventureBox = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.adventureBoxReady === "true") return;

  const cards = Array.from(root.querySelectorAll("[data-box-product]"));
  const counter = root.querySelector("[data-box-counter]");
  const slots = Array.from(root.querySelectorAll("[data-box-slot]"));
  const totalElement = root.querySelector("[data-box-total]");
  const errorElement = root.querySelector("[data-box-error]");
  const submitButton = root.querySelector("[data-box-submit]");
  const picker = root.querySelector("[data-box-picker]");
  const openButton = root.querySelector("[data-box-open]");
  const closeButton = root.querySelector("[data-box-close]");
  if (!cards.length || !counter || slots.length !== 3 || !totalElement || !errorElement || !submitButton || !picker || !openButton || !closeButton) return;

  root.dataset.adventureBoxReady = "true";
  const currencySymbol = root.dataset.currencySymbol || "$";
  const cartAddUrl = root.dataset.cartAddUrl || `${window.Shopify?.routes?.root || "/"}cart/add.js`;
  const cartUrl = root.dataset.cartUrl || `${window.Shopify?.routes?.root || "/"}cart`;
  const boxName = root.dataset.boxName || "Adventure Box";
  const submitLabel = submitButton.textContent.trim();
  const emptySlotLabels = [
    "Choose your first coffee",
    "Choose your second coffee",
    "Choose your third coffee",
  ];
  let selections = [];
  let returnFocus = openButton;

  const close = () => {
    picker.classList.remove("open");
    picker.hidden = true;
    document.body.style.overflow = "";
    document.removeEventListener("keydown", handleKeydown);
    if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus();
  };

  const handleKeydown = (event) => {
    if (event.key === "Escape") {
      close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(picker.querySelectorAll(
      'button:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      picker.querySelector(".adventure-box__panel")?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const open = () => {
    returnFocus = document.activeElement;
    picker.hidden = false;
    picker.classList.add("open");
    document.body.style.overflow = "hidden";
    errorElement.textContent = "";
    errorElement.classList.remove("show");
    document.addEventListener("keydown", handleKeydown);
    closeButton.focus();
  };

  openButton.addEventListener("click", open);
  closeButton.addEventListener("click", close);
  picker.addEventListener("click", (event) => {
    if (event.target === picker) close();
  });

  const money = (cents) => `${currencySymbol}${(cents / 100).toFixed(2)}`;
  const readChoice = (card) => {
    const select = card.querySelector("[data-box-variant]");
    const option = select?.selectedOptions[0];
    if (!select || !option || option.disabled) return null;
    return {
      card,
      productTitle: card.dataset.productTitle || "Coffee",
      variantId: Number.parseInt(select.value, 10),
      variantTitle: option.dataset.variantTitle || option.textContent.trim(),
      price: Number.parseInt(option.dataset.price, 10) || 0,
    };
  };

  const refresh = () => {
    cards.forEach((card) => {
      const selected = selections.some((selection) => selection.card === card);
      const button = card.querySelector("[data-box-toggle]");
      card.classList.toggle("is-selected", selected);
      if (button) {
        button.setAttribute("aria-pressed", String(selected));
        button.textContent = selected ? "Selected — Remove" : "Choose This Coffee";
      }
    });

    slots.forEach((slot, index) => {
      const selection = selections[index];
      const label = slot.querySelector("em");
      slot.classList.toggle("is-filled", Boolean(selection));
      if (!label) return;
      if (!selection) {
        label.textContent = emptySlotLabels[index];
        return;
      }
      const variantLabel = selection.variantTitle && selection.variantTitle !== "Default Title"
        ? ` — ${selection.variantTitle}`
        : "";
      label.textContent = `${selection.productTitle}${variantLabel}`;
    });

    const total = selections.reduce((sum, selection) => sum + selection.price, 0);
    counter.textContent = `${selections.length} of 3 selected`;
    counter.classList.toggle("is-complete", selections.length === 3);
    totalElement.textContent = money(total);
    submitButton.disabled = selections.length !== 3;
  };

  cards.forEach((card) => {
    const toggleButton = card.querySelector("[data-box-toggle]");
    const variantSelect = card.querySelector("[data-box-variant]");
    const priceLabel = card.querySelector("[data-box-price]");
    if (!toggleButton || !variantSelect) return;

    toggleButton.addEventListener("click", () => {
      const selectedIndex = selections.findIndex((selection) => selection.card === card);
      errorElement.textContent = "";
      errorElement.classList.remove("show");

      if (selectedIndex >= 0) {
        selections.splice(selectedIndex, 1);
      } else if (selections.length >= 3) {
        errorElement.textContent = "Your box already has three coffees. Remove one before choosing another.";
        errorElement.classList.add("show");
      } else {
        const choice = readChoice(card);
        if (choice?.variantId) selections.push(choice);
      }
      refresh();
    });

    variantSelect.addEventListener("change", () => {
      const option = variantSelect.selectedOptions[0];
      if (priceLabel && option) priceLabel.textContent = option.dataset.priceLabel || money(Number.parseInt(option.dataset.price, 10) || 0);

      const selectedIndex = selections.findIndex((selection) => selection.card === card);
      if (selectedIndex >= 0) {
        const choice = readChoice(card);
        if (choice?.variantId) selections[selectedIndex] = choice;
      }
      errorElement.textContent = "";
      errorElement.classList.remove("show");
      refresh();
    });
  });

  submitButton.addEventListener("click", async () => {
    if (selections.length !== 3) return;

    const boxId = `ADVENTURE-${Date.now().toString(36).toUpperCase()}`;
    const items = selections.map((selection, index) => ({
      id: selection.variantId,
      quantity: 1,
      properties: {
        "Adventure box": boxName,
        "Box choice": `${index + 1} of 3`,
        "_Adventure box ID": boxId,
      },
    }));

    submitButton.disabled = true;
    submitButton.setAttribute("aria-busy", "true");
    submitButton.textContent = "Packing Your Box…";
    errorElement.textContent = "";
    errorElement.classList.remove("show");

    try {
      const response = await fetch(cartAddUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || result.message || "The Adventure Box could not be added.");
      }
      window.location.assign(cartUrl);
    } catch (error) {
      errorElement.textContent = error.message || "Something went wrong while packing your box. Please try again.";
      errorElement.classList.add("show");
      submitButton.disabled = false;
      submitButton.removeAttribute("aria-busy");
      submitButton.textContent = submitLabel;
    }
  });

  root.adventureBoxCleanup = () => {
    document.removeEventListener("keydown", handleKeydown);
    if (!picker.hidden) document.body.style.overflow = "";
  };
  refresh();
};

const initAdventureBoxes = (scope) => {
  const roots = scope.matches?.("[data-adventure-box]")
    ? [scope]
    : Array.from(scope.querySelectorAll?.("[data-adventure-box]") || []);
  roots.forEach(initAdventureBox);
};

initAdventureBoxes(document);
document.addEventListener("shopify:section:load", (event) => initAdventureBoxes(event.target));
document.addEventListener("shopify:section:unload", (event) => {
  const root = event.target.matches?.("[data-adventure-box]")
    ? event.target
    : event.target.querySelector?.("[data-adventure-box]");
  root?.adventureBoxCleanup?.();
});
